// @rpg-unit/shop-presets: three kinds of shop's trading terms (tier 3; composition framework, decision 0077): a general
// store and a smith (cut from spike 002's merchant and smith, `content/tables/shops.json`) and an apothecary (new). A
// preset is what a kind of shop buys (item tags), the fraction of an item's price it pays, and the discounts it gives
// by its host faction's standing; a game's shop row names a preset, its host NPC, its faction and its stock. The terms
// map onto @rpg/items' ShopDef (`toShopDef`) and @rpg/story's condition language for the discounts
// (`discountConditions`: `standing.<faction>.discount >= <perk>`).
import type { ShopDef } from "@rpg/items";
import table from "./content/tables/shop-presets.json";
import schema from "./content/schemas/shop-presets.schema.json";

/** This unit's version (the manifest's and package.json's). */
export const SHOP_PRESETS_VERSION = "0.1.0" as const;

/** One preset, as its schema describes it (`src/content/schemas/shop-presets.schema.json`). */
export interface ShopPreset {
  id: string;
  /** Item tags it buys (any of them). */
  buys: string[];
  /** The fraction of an item's price it pays. */
  sellRate: number;
  /** A discount while the host faction's standing discount perk is at least `whenPerk`. */
  discounts?: { whenPerk: number; off: number }[];
  /** Why it is tuned this way. */
  reason?: string;
}

/** The presets as a content table (`{ table: "shop-presets", version, rows }`). */
export const SHOP_PRESETS_TABLE = table as { table: "shop-presets"; version: number; rows: ShopPreset[] };

/** The table's row schema. */
export const SHOP_PRESETS_SCHEMA = schema as Record<string, unknown>;

/** The content this unit adds to a game, in @rpg/content's shape. */
export const SHOP_PRESET_CONTENT = {
  package: "@rpg-unit/shop-presets",
  version: SHOP_PRESETS_VERSION,
  tables: [SHOP_PRESETS_TABLE],
  schemas: { "shop-presets": SHOP_PRESETS_SCHEMA },
} as const;

/** A shop on @rpg/items from a preset and the game's stock. */
export function toShopDef(id: string, preset: ShopPreset, stock: ShopDef["stock"]): ShopDef {
  return { id, stock, buys: preset.buys, sellRate: preset.sellRate };
}

/** A preset's discounts as conditions in @rpg/story's language, for a host faction (`town` → `standing.town.discount >= 0.1`). */
export function discountConditions(preset: ShopPreset, faction: string): { when: string; off: number }[] {
  return (preset.discounts ?? []).map((d) => ({ when: `standing.${faction}.discount >= ${d.whenPerk}`, off: d.off }));
}

/** The least margin a preset keeps between what it pays and what it charges at its deepest discount, as a fraction of
 *  the price: below it, @rpg/items' "sell below the buy price" clamp, not the rate, would set sale prices. */
export const MIN_MARGIN = 0.1;

/** Problems with a set of presets that the schema can't say (empty: none): ids unique; discounts ascending (a higher
 *  perk never gives less off); the rate leaves `MIN_MARGIN` below the deepest discounted price. */
export function checkShopPresets(rows: readonly ShopPreset[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    if (seen.has(r.id)) out.push(`preset "${r.id}" is defined twice`);
    seen.add(r.id);
    const ds = r.discounts ?? [];
    for (let i = 1; i < ds.length; i++) if (!(ds[i]!.whenPerk > ds[i - 1]!.whenPerk && ds[i]!.off >= ds[i - 1]!.off)) out.push(`preset "${r.id}": discount ${i} must need a higher perk and give at least as much off as the one before`);
    const deepest = Math.max(0, ...ds.map((d) => d.off));
    if (r.sellRate > 1 - deepest - MIN_MARGIN + 1e-9) out.push(`preset "${r.id}": pays ${r.sellRate} of the price, within ${MIN_MARGIN} of its deepest discounted price (${Math.round((1 - deepest) * 100) / 100})`);
  }
  return out;
}

/** The perceptual knobs (rule 13). */
export const SHOP_PRESET_KNOBS = {
  generosity: "pays more or less for what the player sells: scales every sell rate (kept a margin below the deepest discounted price). 1 as tuned.",
  friendliness: "deeper or shallower standing discounts: scales every discount's `off`. 1 as tuned; 0 no discounts.",
} as const;

/** Knob values; each defaults to 1. */
export interface ShopKnobs {
  generosity?: number;
  friendliness?: number;
}

const round = (x: number): number => Math.round(x * 1e6) / 1e6;

/** The presets turned by the knobs (new rows). All at 1: equal to the input. The sell rate is capped `MIN_MARGIN`
 *  below the deepest discounted price, so a generous shop still never pays what it charges. */
export function tuneShopPresets(rows: readonly ShopPreset[], knobs: ShopKnobs = {}): ShopPreset[] {
  const g = knobs.generosity ?? 1;
  const f = knobs.friendliness ?? 1;
  if (!(g >= 0) || !(f >= 0)) throw new Error(`shop presets: knobs must be ≥ 0 (got ${g}, ${f})`);
  return rows.map((r) => {
    const out: ShopPreset = structuredClone(r);
    if (f !== 1 && out.discounts) out.discounts = out.discounts.map((d) => ({ ...d, off: round(Math.min(0.9, d.off * f)) }));
    if (g !== 1) {
      const deepest = Math.max(0, ...(out.discounts ?? []).map((d) => d.off));
      out.sellRate = round(Math.max(0, Math.min(r.sellRate * g, 1 - deepest - MIN_MARGIN)));
    }
    return out;
  });
}
